import {Component, OnInit, computed, ChangeDetectionStrategy} from "@angular/core"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {
    UVControlPoint,
    PositionControlPoint,
    ObjectPath,
    SceneNodes,
    getControlPointPartNumber,
    isControlPointPart,
    TemplateNode,
    ObjectPathPart,
    getRootTemplateNodeForObjectPath,
} from "@cm/template-nodes"
import {Vector3} from "@cm/math"
import {objectPathPartEquals, SceneNodePartIntersection, SceneNodePartClickEvent} from "@app/template-editor/services/scene-manager.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-base-curve-inspector",
    imports: [],
    template: "",
})
export class BaseCurveInspectorComponent<T extends TemplateNode<{controlPoints: UVControlPoint[] | PositionControlPoint[]}>>
    extends BaseInspectorComponent<T extends TemplateNode<{controlPoints: infer W}> ? T & TemplateNode<{controlPoints: W}> : never>
    implements OnInit
{
    readonly $controlPoints = computed(() => this.$parameters().controlPoints)
    readonly $selectedControlPoints = computed(() => {
        const filteredNodeParts = this.sceneManagerService
            .$selectedRootNodeParts()

            .filter((nodePart): nodePart is {templateNode: T; part: `controlPoint${number}`} => {
                const {templateNode, part} = nodePart
                if (templateNode !== this.$node() || !isControlPointPart(part)) return false
                const index = getControlPointPartNumber(part)
                return this.$parameters().controlPoints[index] !== undefined
            })

        return new Set(filteredNodeParts.map(({part}) => getControlPointPartNumber(part)).sort())
    })

    override ngOnInit() {
        super.ngOnInit()

        this.sceneManagerService.templateTreeChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((differences) => {
            const node = this.$node()

            const changes = differences.modifiedNodes.get(node)
            if (changes) {
                const selectedNodePartsToRemove = this.sceneManagerService.$selectedRootNodeParts().filter(({templateNode, part}) => {
                    if (templateNode !== node || !isControlPointPart(part)) return false

                    const index = getControlPointPartNumber(part)
                    const change = changes.find((change) => change.path === `controlPoints.${index}`)
                    if (!change) return false

                    return change.newValue === undefined
                })

                if (selectedNodePartsToRemove.length > 0) {
                    const partsToRemove: ObjectPathPart[] = selectedNodePartsToRemove.map((snp) => ({path: [snp.templateNode], part: snp.part}))
                    const filtered = this.sceneManagerService.$selectedObjectPathParts().filter((p) => !partsToRemove.some((r) => objectPathPartEquals(p, r)))
                    const stillHasNodeAsRoot = filtered.some((p) => getRootTemplateNodeForObjectPath(p.path) === node)
                    this.sceneManagerService.selectNodes(stillHasNodeAsRoot ? filtered : [...filtered, {path: [node], part: "root"}])
                }
            }
        })
    }

    addPointImpl(_node: T):
        | {
              canAdd: true
              getNewControlPointsWithInsertionPosition: (sceneNodePartClickEvent: SceneNodePartClickEvent) =>
                  | {
                        controlPoints: T["parameters"]["controlPoints"]
                        insertionPosition: number | undefined
                    }
                  | undefined
              prefixPath: ObjectPath | undefined
          }
        | {canAdd: false} {
        throw new Error("Derived class expected to implement addPointImpl")
    }

    addPoint() {
        const node = this.$node()

        const addPointAttempt = this.addPointImpl(node)

        if (!addPointAttempt.canAdd) return

        this.sceneManagerService
            .watchForClickedSceneNodePart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((sceneNodePartClickEvent) => {
                const newControlPointsWithInsertionPosition = addPointAttempt.getNewControlPointsWithInsertionPosition(sceneNodePartClickEvent)

                if (!newControlPointsWithInsertionPosition) return
                const {controlPoints, insertionPosition} = newControlPointsWithInsertionPosition

                this.sceneManagerService.modifyTemplateGraph(() => {
                    node.updateParameters({controlPoints})
                })

                const partsToRemove: ObjectPathPart[] = this.sceneManagerService
                    .$selectedRootNodeParts()
                    .filter((snp) => snp.templateNode === node)
                    .map((snp) => ({path: [snp.templateNode], part: snp.part}))
                const filtered = this.sceneManagerService.$selectedObjectPathParts().filter((p) => !partsToRemove.some((r) => objectPathPartEquals(p, r)))
                this.sceneManagerService.selectNodes([
                    ...filtered,
                    {
                        path: [...(addPointAttempt.prefixPath ?? []), node],
                        part: `controlPoint${insertionPosition !== undefined ? insertionPosition : controlPoints.length - 1}`,
                    },
                ])
            })
    }

    onMouseEnter(index: number) {
        const node = this.$node()
        this.sceneManagerService.hoverNodes([{path: [node], part: `controlPoint${index}`}])
    }

    onMouseLeave() {
        this.sceneManagerService.hoverNodes(undefined)
    }

    deletePoints() {
        const selectedControlPoints = this.$selectedControlPoints()
        if (selectedControlPoints.size === 0) return

        this.deletePointsForIndices(selectedControlPoints)
    }

    deletePointsForIndices(controlPointIndices: Set<number> | number[]) {
        const controlPointIndicesSet = controlPointIndices instanceof Set ? controlPointIndices : new Set(controlPointIndices)

        const node = this.$node()

        this.sceneManagerService.modifyTemplateGraph(() => {
            node.updateParameters({
                controlPoints: [...node.parameters.controlPoints].filter(
                    (_, index) => !controlPointIndicesSet.has(index),
                ) as typeof node.parameters.controlPoints,
            })
        })
    }

    getMeshCurveControlForNode(node: T) {
        const meshCurveControl = this.sceneManagerService.getSceneNodes(node).find(SceneNodes.MeshCurveControl.is)

        if (!meshCurveControl) throw new Error("MeshCurveControl not found")
        return meshCurveControl
    }

    getInsertionPositionForAddedControlPoint(curveIntersection: SceneNodePartIntersection | undefined, addedPointPosition: Vector3) {
        if (curveIntersection) {
            const meshCurveControl = curveIntersection.sceneNodePart.sceneNode
            if (!SceneNodes.MeshCurveControl.is(meshCurveControl)) return undefined
            if (!meshCurveControl.completeCurveData.reified) return undefined
            const {points, segments} = meshCurveControl.completeCurveData.reified

            const pointsArray: Vector3[] = []
            for (let i = 0; i < points.length; i += 3) pointsArray.push(new Vector3(points[i], points[i + 1], points[i + 2]))

            const minDistanceIndex = pointsArray.reduce((minIndex, currentPoint, index) => {
                const distance = addedPointPosition.distance(currentPoint)
                return distance < addedPointPosition.distance(pointsArray[minIndex]) ? index : minIndex
            }, 0)

            return Math.floor(segments[minDistanceIndex]) + 1
        } else return undefined
    }
}
